// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyFile } from "@/types";
import FileCard from "@/UI/cards/FileCard";
import PhotoCard from "@/UI/cards/PhotoCard";
import EmptyState from "@/UI/components/EmptyState";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import FilePreviewPopup from "@/UI/popups/FilePreviewPopup";
import RenamePopup from "@/UI/popups/RenamePopup";
import ReplaceFilePopup from "@/UI/popups/ReplaceFilePopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    /** The record these files hang off — `Project`, `Unit`, and every owner model after them. */
    ownerModel: string;
    ownerId: string;
    /**
     * Opens the screen's own upload panel. Only used by the empty state: uploading is the bar's
     * button everywhere else, and a tab with nothing in it is the one place that button is not the
     * obvious next thing to press.
     */
    onUpload?: () => void;
};

/**
 * A record's files, shown **where the record is**.
 *
 * Uploading is the screen's button, not this component's: the add button belongs in the one bar at
 * the top with the tabs, and the screen already knows which record it is looking at. This list hears
 * about a finished upload from the tray, like the drive does.
 *
 * Sending somebody to the drive to see a project's documents was a navigation away from the thing
 * they were working on, and back again for each file. The drive is still the whole picture — every
 * file, every folder, the storage bill — and this is the same rows filtered to one record, with the
 * same actions on them, so neither screen is a second implementation of the other.
 *
 * Uploading goes through `useUploads` like everywhere else: the record already exists, so the files
 * are handed to the tray and this list reloads when the tray says one landed.
 *
 * **The record's own files come first, and they are not the same thing as its folder.** A project's
 * gallery and the loose documents filed under that project were indistinguishable in the data until
 * `File.slot` existed — both were simply "files owned by this project" — so a tab showing them
 * together could only sort them by category, which answers a different question. Now each slot is
 * its own titled strip, read sideways because that is how a set of pictures is read, and everything
 * with no slot sits in the drive's own grid underneath as what it is: general files.
 *
 * One request, split here. The old version made two by category and could not have grouped by slot
 * without a third; this asks for the record's files once and sorts them in the browser, which is a
 * handful of rows either way.
 *
 * It is not paged. A record holds the paper for one project or one flat, and a pager under eight
 * thumbnails is furniture. The request asks for the API's maximum; `.todo` holds the day that stops
 * being enough.
 */
export default function RecordFiles({ ownerModel, ownerId, onUpload }: Props) {
    const t = useTranslation();
    const { isOwner, role } = usePermission();
    const addComp = usePopup((state) => state.addComp);
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);

    const files = useFetch<{ files: TyFile[]; total: number }>("/drive", {}, false);
    const action = useFetch("/drive", {}, false);
    const canDelete = isOwner || role === "manager";

    const load = () => files.invoke({ query: { ownerModel, ownerId, limit: constants.maxPageSize } });

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerModel, ownerId]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    // The tray may well be on another screen by the time an upload finishes.
    useEffect(() => {
        if (!uploadsFinishedAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadsFinishedAt]);

    const openFile = (file: TyFile, list: TyFile[]) =>
        addComp({
            name: "file-preview",
            Component: FilePreviewPopup,
            anim: "fade",
            details: {
                files: list,
                index: Math.max(
                    0,
                    list.findIndex((item) => item._id === file._id)
                ),
            },
        });

    const openActions = (file: TyFile, anchor: DOMRect) => {
        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            {
                key: "rename",
                label: t.drive.actions.rename,
                icon: "edit",
                onSelect: () =>
                    addComp({
                        name: "rename-file",
                        Component: RenamePopup,
                        anim: "scale",
                        details: { title: t.drive.renameTitle, value: file.name, path: `/drive/${file._id}`, onDone: load },
                    }),
            },
            {
                key: "replace",
                label: t.drive.actions.replace,
                icon: "refresh",
                onSelect: () => addComp({ name: "replace-file", Component: ReplaceFilePopup, place: "end", anim: "endSlide", details: { file } }),
            },
        ];

        // Deleting a file is the drive's one exception to "anyone may delete": there is no trash for
        // a file, so it really is gone and it really did cost storage.
        if (canDelete) {
            actions.push({
                key: "remove",
                label: t.drive.actions.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.drive.actions.remove,
                            message: t.drive.confirmRemove.replace("{name}", file.name),
                            confirmLabel: t.drive.actions.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/drive/${file._id}` }),
                        },
                    }),
            });
        }

        addComp({
            name: "file-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /**
     * Each slot walks its own arrows in the viewer, so paging through a gallery never wanders into
     * the ID copies. Ordered by `fileSlots` rather than by whatever came back first, so the strips
     * do not reshuffle themselves as files are added.
     */
    const rows = files.data?.files ?? [];
    const slots = constants.fileSlots
        .map((slot) => ({ slot, list: rows.filter((file) => file.slot === slot) }))
        .filter((group) => group.list.length > 0);
    const loose = rows.filter((file) => !file.slot);
    const loading = files.loading;
    const error = files.error ?? action.error;

    return (
        <div className="record-files">
            {loading && !rows.length && <p className="record-files__state">{t.common.loading}</p>}
            {error && <p className="record-files__state record-files__state--error">{error}</p>}

            {/* What the record **is** made of — one strip per kind, read sideways. */}
            {slots.map((group) => (
                <section key={group.slot} className="record-files__slot">
                    <h2 className="record-files__title record-files__title--slot">
                        {t.drive.slots[group.slot]}
                        <span className="record-files__count">{group.list.length}</span>
                    </h2>

                    <div className="record-files__strip">
                        {group.list.map((file) =>
                            file.category === "image" ? (
                                <PhotoCard
                                    key={file._id}
                                    file={file}
                                    onOpen={() => openFile(file, group.list)}
                                    onActions={(anchor) => openActions(file, anchor)}
                                />
                            ) : (
                                <div key={file._id} className="record-files__strip-item">
                                    <FileCard
                                        file={file}
                                        onOpen={() => openFile(file, group.list)}
                                        onActions={(anchor) => openActions(file, anchor)}
                                    />
                                </div>
                            )
                        )}
                    </div>
                </section>
            ))}

            {/*
             * Everything merely filed here. The drive's own grid, because that is what these are.
             *
             * **Drawn only when it has something in it.** A record with a full gallery and no loose
             * documents used to end on a heading over a grey sentence, which read as a section that
             * had failed rather than one that does not apply. When the record has no files at all the
             * tab says so once, underneath, instead of saying it per section.
             */}
            {loose.length > 0 && (
                <section className="record-files__section">
                    <h2 className="record-files__title">{t.drive.generalFiles}</h2>

                    <div className="record-files__grid">
                        {loose.map((file) =>
                            file.category === "image" ? (
                                <PhotoCard
                                    key={file._id}
                                    file={file}
                                    onOpen={() => openFile(file, loose)}
                                    onActions={(anchor) => openActions(file, anchor)}
                                />
                            ) : (
                                <FileCard
                                    key={file._id}
                                    file={file}
                                    onOpen={() => openFile(file, loose)}
                                    onActions={(anchor) => openActions(file, anchor)}
                                />
                            )
                        )}
                    </div>
                </section>
            )}

            {!loading && !error && !rows.length && (
                <EmptyState
                    icon="drive"
                    title={t.drive.emptyRecord}
                    description={t.drive.emptyRecordHint}
                    actionLabel={onUpload ? t.drive.upload : undefined}
                    onAction={onUpload}
                />
            )}
        </div>
    );
}
